import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import BASE_URL from '../../../../config/config';

// Campos que el formulario puede tocar. El PUT solo envía estos: mandar el
// objeto entero devuelto por la API arrastraba campos que el backend no debe
// recibir (entre ellos la contraseña, que se rehasheaba y dejaba al usuario
// sin acceso).
export const CAMPOS_EDITABLES = [
  'nombre',
  'apellidos',
  'email',
  'telefono',
  'webs_del_cliente',
  'role',
  'estado',
  'dpto_tecnico',
];

const soloEditables = (datos) => CAMPOS_EDITABLES.reduce((acc, campo) => {
  acc[campo] = datos[campo] ?? '';
  return acc;
}, {});

/**
 * Carga y estado del detalle de usuario: datos, tickets, auditoría y servicios.
 * Todo se pide en paralelo en un único efecto para no encadenar cascadas.
 */
const useDetalleUsuario = (usuario) => {
  const token = localStorage.getItem('AuthToken');

  const viewerRole = useMemo(() => {
    try {
      return token ? jwtDecode(token).role : null;
    } catch (err) {
      return null;
    }
  }, [token]);

  const esAdmin = viewerRole === 'admin';

  const [usuarioData, setUsuarioData] = useState(null);
  const [form, setForm] = useState({});
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [tickets, setTickets] = useState([]);
  const [cargandoTickets, setCargandoTickets] = useState(true);

  const [auditoria, setAuditoria] = useState([]);
  const [cargandoAuditoria, setCargandoAuditoria] = useState(true);

  const [catalogoServicios, setCatalogoServicios] = useState([]);
  const [serviciosUsuario, setServiciosUsuario] = useState([]);
  const [serviciosAsignados, setServiciosAsignados] = useState([]);
  const [serviciosOriginales, setServiciosOriginales] = useState([]);
  const [cargandoServicios, setCargandoServicios] = useState(true);

  const cabeceras = useMemo(() => ({ headers: { Authorization: token } }), [token]);

  useEffect(() => {
    if (!usuario) {
      setError('No se ha indicado ningún usuario.');
      setCargando(false);
      return;
    }

    let cancelado = false;

    const cargar = async () => {
      setCargando(true);

      // Cada bloque falla por su cuenta: que no haya auditoría no debe impedir
      // ver la ficha.
      const peticiones = [
        axios.get(`${BASE_URL}/usuario/info/${usuario}`, cabeceras),
        axios.get(`${BASE_URL}/tickets/total/${usuario}`).catch(() => null),
        axios.get(`${BASE_URL}/usuario/auditoria/${usuario}`, cabeceras).catch(() => null),
        axios.get(`${BASE_URL}/servicios/usuario/${usuario}`, cabeceras).catch(() => null),
        esAdmin
          ? axios.get(`${BASE_URL}/servicios`, cabeceras).catch(() => null)
          : Promise.resolve(null),
      ];

      try {
        const [respUsuario, respTickets, respAuditoria, respServicios, respCatalogo] =
          await Promise.all(peticiones);

        if (cancelado) return;

        const datos = respUsuario.data.usuarios || respUsuario.data.usuario || respUsuario.data;
        setUsuarioData(datos);
        setForm(soloEditables(datos));
        setError(null);

        const listaTickets = respTickets?.data?.tickets || [];
        setTickets([...listaTickets].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));

        setAuditoria(respAuditoria?.data?.success ? (respAuditoria.data.registros || []) : []);

        const asignados = respServicios?.data?.success ? (respServicios.data.servicios || []) : [];
        const ids = asignados.map((s) => s.id);
        setServiciosUsuario(asignados);
        setServiciosAsignados(ids);
        setServiciosOriginales(ids);

        setCatalogoServicios(respCatalogo?.data?.success ? (respCatalogo.data.servicios || []) : []);
      } catch (err) {
        if (cancelado) return;
        console.error('Error al cargar el detalle del usuario:', err);
        setError('No se pudo cargar el usuario.');
      } finally {
        if (cancelado) return;
        setCargando(false);
        setCargandoTickets(false);
        setCargandoAuditoria(false);
        setCargandoServicios(false);
      }
    };

    cargar();
    return () => { cancelado = true; };
  }, [usuario, cabeceras, esAdmin]);

  // ── Cambios pendientes ────────────────────────────────────────────────────
  // Se calculan una vez por render y se reutilizan: la barra de guardado, el
  // aviso al salir y los contadores de las pestañas leen de aquí.

  const camposCambiados = useMemo(() => {
    if (!usuarioData) return [];
    return CAMPOS_EDITABLES.filter((campo) => (form[campo] ?? '') !== (usuarioData[campo] ?? ''));
  }, [form, usuarioData]);

  const serviciosCambiados = useMemo(() => {
    if (serviciosAsignados.length !== serviciosOriginales.length) return true;
    const originales = new Set(serviciosOriginales);
    return serviciosAsignados.some((id) => !originales.has(id));
  }, [serviciosAsignados, serviciosOriginales]);

  const totalCambios = camposCambiados.length + (serviciosCambiados ? 1 : 0);
  const hayCambios = totalCambios > 0;

  const actualizarCampo = useCallback((campo, valor) => {
    setForm((prev) => ({ ...prev, [campo]: valor }));
  }, []);

  const alternarServicio = useCallback((id) => {
    setServiciosAsignados((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  }, []);

  const descartarCambios = useCallback(() => {
    if (usuarioData) setForm(soloEditables(usuarioData));
    setServiciosAsignados(serviciosOriginales);
  }, [usuarioData, serviciosOriginales]);

  /**
   * Guarda los campos editables y, si el admin ha tocado los servicios, sus
   * asignaciones. Devuelve true si todo fue bien.
   */
  const guardar = useCallback(async () => {
    const payload = soloEditables(form);

    const resp = await axios.put(`${BASE_URL}/usuario/update/${usuario}`, payload, cabeceras);
    const actualizado = resp.data.user || resp.data.usuario || resp.data;
    setUsuarioData(actualizado);
    setForm(soloEditables(actualizado));

    if (esAdmin && serviciosCambiados) {
      await axios.put(`${BASE_URL}/servicios/usuario/${usuario}`, { servicios: serviciosAsignados }, cabeceras);
      setServiciosOriginales(serviciosAsignados);
      setServiciosUsuario(catalogoServicios.filter((s) => serviciosAsignados.includes(s.id)));
    }

    return true;
  }, [form, usuario, cabeceras, esAdmin, serviciosCambiados, serviciosAsignados, catalogoServicios]);

  /**
   * Acción rápida del hero. Va por su cuenta y no arrastra lo que haya sin
   * guardar en el formulario: solo toca el estado.
   */
  const cambiarEstado = useCallback(async (nuevoEstado) => {
    const resp = await axios.put(
      `${BASE_URL}/usuario/update/${usuario}`,
      { ...soloEditables(usuarioData), estado: nuevoEstado },
      cabeceras,
    );
    const actualizado = resp.data.user || resp.data.usuario || resp.data;
    setUsuarioData(actualizado);
    // El formulario sigue con sus cambios pendientes, pero el estado ya está
    // guardado: se sincroniza para que no aparezca como pendiente.
    setForm((prev) => ({ ...prev, estado: actualizado.estado }));
    return actualizado;
  }, [usuario, usuarioData, cabeceras]);

  return {
    token,
    viewerRole,
    esAdmin,

    usuarioData,
    setUsuarioData,
    form,
    actualizarCampo,
    cargando,
    error,

    tickets,
    cargandoTickets,
    auditoria,
    cargandoAuditoria,

    catalogoServicios,
    serviciosUsuario,
    serviciosAsignados,
    alternarServicio,
    cargandoServicios,

    camposCambiados,
    serviciosCambiados,
    totalCambios,
    hayCambios,
    descartarCambios,
    guardar,
    cambiarEstado,
  };
};

export default useDetalleUsuario;
